import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import {
  Package,
  Pencil,
  Trash2,
  X,
  CheckCircle2,
  XCircle,
  Cpu,
  Plus,
  Minus,
  ChevronDown,
  ChevronRight,
  Clock,
  Rocket,
  Loader,
} from "lucide-react";

interface App {
  appId: string;
  displayName: string;
  description: string;
  repo: string;
  currentVersion: string;
  deviceIds: string[];
  config: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

interface Device {
  deviceId: string;
  lastSeen?: string;
}

export function AppsPage() {
  const [apps, setApps] = useState<App[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingApp, setEditingApp] = useState<App | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [devices, setDevices] = useState<Device[]>([]);
  const [devicesLoaded, setDevicesLoaded] = useState(false);
  const [deploying, setDeploying] = useState<string | null>(null);

  const fetchApps = () => {
    api.get("/apps").then(({ data }) => {
      setApps(data.apps ?? []);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  const fetchDevices = () => {
    if (devicesLoaded) return;
    api.get("/devices").then(({ data }) => {
      setDevices(data.devices ?? []);
      setDevicesLoaded(true);
    }).catch(() => setDevicesLoaded(true));
  };

  useEffect(() => { fetchApps(); }, []);

  const handleDelete = async (appId: string) => {
    if (!confirm(`Delete app "${appId}"? This removes the app from the registry. It does not uninstall it from devices.`)) return;
    try {
      await api.delete(`/apps/${appId}`);
      setSuccess(`Deleted ${appId}`);
      fetchApps();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const handleAddDevice = async (appId: string, deviceId: string) => {
    try {
      await api.post(`/apps/${appId}/devices`, { deviceIds: [deviceId] });
      setSuccess(`Added ${deviceId} to ${appId}`);
      fetchApps();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const handleRemoveDevice = async (appId: string, deviceId: string) => {
    if (!confirm(`Remove ${deviceId} from ${appId}?\n\nThis will uninstall the app from the device.`)) return;
    try {
      const { data } = await api.delete(`/apps/${appId}/devices/${deviceId}`);
      const msg = data.uninstallSent
        ? `Removed ${deviceId} from ${appId} — uninstall command sent to device`
        : `Removed ${deviceId} from ${appId} — device may be offline, manual cleanup needed`;
      setSuccess(msg);
      fetchApps();
      setTimeout(() => setSuccess(null), 5000);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const handleDeploy = async (app: App) => {
    const deviceCount = (app.deviceIds ?? []).length;
    if (!deviceCount) {
      setError("No devices assigned. Add devices first.");
      return;
    }
    if (!app.currentVersion) {
      setError("No version available. Deploy from CI first to upload an artifact.");
      return;
    }
    if (!confirm(`Deploy ${app.displayName} v${app.currentVersion.slice(0, 8)} to ${deviceCount} device${deviceCount !== 1 ? "s" : ""}?`)) return;

    setDeploying(app.appId);
    try {
      const { data } = await api.post("/deploys", {
        target: "shell",
        appId: app.appId,
        artifactKey: `deploys/${app.repo}/${app.currentVersion}/shell.tar.gz`,
        version: app.currentVersion,
        repo: app.repo,
      });
      setSuccess(`Deploy created: ${data.deviceCount} device${data.deviceCount !== 1 ? "s" : ""} targeted`);
      setTimeout(() => setSuccess(null), 5000);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setDeploying(null);
    }
  };

  const toggleExpand = (appId: string) => {
    if (expandedId === appId) {
      setExpandedId(null);
    } else {
      setExpandedId(appId);
      fetchDevices();
    }
  };

  return (
    <div className="px-8 pt-8 pb-12 max-w-4xl">
      <div>
        <h1 className="text-2xl font-semibold text-surface-900">Apps</h1>
        <p className="mt-1 text-sm text-surface-500">
          {loading ? "Loading..." : apps.length === 0
            ? "No apps yet. Apps appear here automatically when first deployed."
            : `${apps.length} app${apps.length !== 1 ? "s" : ""} registered`}
        </p>
      </div>

      {success && (
        <div className="mt-4 p-3 rounded-lg bg-success-100 text-success-500 text-sm flex items-center gap-2">
          <CheckCircle2 size={16} /> {success}
          <button onClick={() => setSuccess(null)} className="ml-auto text-xs opacity-60 hover:opacity-100 cursor-pointer">Dismiss</button>
        </div>
      )}

      {error && (
        <div className="mt-4 p-3 rounded-lg bg-danger-100 text-danger-500 text-sm flex items-center gap-2">
          <XCircle size={16} /> {error}
          <button onClick={() => setError(null)} className="ml-auto text-xs opacity-60 hover:opacity-100 cursor-pointer">Dismiss</button>
        </div>
      )}

      {/* Edit form */}
      {editingApp && (
        <AppEditForm
          app={editingApp}
          onSave={() => { setEditingApp(null); fetchApps(); }}
          onCancel={() => { setEditingApp(null); setError(null); }}
          onError={setError}
        />
      )}

      {/* Empty state */}
      {!loading && apps.length === 0 && (
        <div className="mt-8 text-center py-16 bg-surface-0 rounded-xl border border-surface-200">
          <Package size={40} className="mx-auto text-surface-400 mb-4" strokeWidth={1.2} />
          <h2 className="text-lg font-medium text-surface-800">No apps yet</h2>
          <p className="mt-2 text-sm text-surface-500 max-w-md mx-auto">
            Apps are registered automatically when they deploy for the first time.
            Include <span className="font-mono text-surface-600">appId</span> in
            your deploy request and the app will appear here.
          </p>
        </div>
      )}

      {/* Apps list */}
      {!loading && apps.length > 0 && (
        <div className="mt-6 space-y-4">
          {apps.map((app) => {
            const expanded = expandedId === app.appId;
            const deviceCount = (app.deviceIds ?? []).length;
            const hasDevices = deviceCount > 0;

            return (
              <div key={app.appId} className="bg-surface-0 rounded-xl border border-surface-200">
                {/* Header */}
                <div className="p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-brand-50 flex items-center justify-center flex-shrink-0">
                        <Package size={20} className="text-brand-500" strokeWidth={1.5} />
                      </div>
                      <div>
                        <h3 className="text-base font-semibold text-surface-900">
                          {app.displayName}
                        </h3>
                        <div className="text-xs font-mono text-surface-400 mt-0.5">{app.appId}</div>
                        {app.description && (
                          <p className="text-sm text-surface-500 mt-2">{app.description}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => { setEditingApp(app); setError(null); }}
                        className="p-2 text-surface-400 hover:text-surface-700 hover:bg-surface-100 rounded-lg transition-colors cursor-pointer"
                        title="Edit"
                      >
                        <Pencil size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(app.appId)}
                        className="p-2 text-surface-400 hover:text-danger-500 hover:bg-danger-100 rounded-lg transition-colors cursor-pointer"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>

                  <div className="mt-4 flex items-center gap-6 text-xs text-surface-400">
                    {app.repo && (
                      <span>Repo: <span className="font-mono text-surface-600">{app.repo}</span></span>
                    )}
                    {app.currentVersion && (
                      <span>Version: <span className="font-mono text-surface-600">{app.currentVersion.slice(0, 8)}</span></span>
                    )}
                    <span>Created: {new Date(app.createdAt).toLocaleDateString()}</span>
                  </div>

                  {/* Device assignment section */}
                  <button
                    onClick={() => toggleExpand(app.appId)}
                    className="mt-4 flex items-center gap-2 text-sm cursor-pointer hover:text-surface-700 transition-colors"
                  >
                    {expanded ? <ChevronDown size={14} className="text-surface-400" /> : <ChevronRight size={14} className="text-surface-400" />}
                    <Cpu size={14} className={hasDevices ? "text-success-500" : "text-warning-500"} />
                    <span className={hasDevices ? "text-surface-700 font-medium" : "text-warning-600 font-medium"}>
                      {hasDevices
                        ? `${deviceCount} device${deviceCount !== 1 ? "s" : ""} assigned`
                        : "No devices assigned"}
                    </span>
                    {!hasDevices && (
                      <span className="text-xs text-warning-500 ml-1">— deploys will be queued until devices are assigned</span>
                    )}
                  </button>
                </div>

                {/* Expanded device assignment */}
                {expanded && (
                  <div className="px-5 pb-5 border-t border-surface-200 pt-4">
                    {/* Currently assigned devices */}
                    {hasDevices && (
                      <div className="mb-4">
                        <div className="text-[11px] text-surface-400 uppercase tracking-wider font-semibold mb-2">
                          Assigned Devices
                        </div>
                        <div className="space-y-2">
                          {(app.deviceIds ?? []).map((deviceId) => (
                            <div key={deviceId} className="flex items-center justify-between py-2 px-3 rounded-lg bg-surface-50 border border-surface-200">
                              <div className="flex items-center gap-3">
                                <Cpu size={14} className="text-brand-500" />
                                <span className="text-sm font-mono text-surface-700">{deviceId}</span>
                              </div>
                              <button
                                onClick={() => handleRemoveDevice(app.appId, deviceId)}
                                className="flex items-center gap-1 px-2 py-1 text-xs text-danger-500 hover:bg-danger-100 rounded transition-colors cursor-pointer"
                                title="Remove device"
                              >
                                <Minus size={12} />
                                Remove
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Available devices to add */}
                    <div>
                      <div className="text-[11px] text-surface-400 uppercase tracking-wider font-semibold mb-2">
                        Available Devices
                      </div>
                      {!devicesLoaded ? (
                        <p className="text-sm text-surface-400">Loading devices...</p>
                      ) : (() => {
                        const assignedSet = new Set(app.deviceIds ?? []);
                        const available = devices.filter((d) => !assignedSet.has(d.deviceId));
                        if (available.length === 0) {
                          return (
                            <p className="text-sm text-surface-400">
                              {devices.length === 0
                                ? "No devices in fleet. Register a device in the Setup page first."
                                : "All devices are already assigned to this app."}
                            </p>
                          );
                        }
                        return (
                          <div className="space-y-2">
                            {available.map((device) => {
                              const isOnline = device.lastSeen
                                ? Date.now() - new Date(device.lastSeen).getTime() < 120_000
                                : false;
                              return (
                                <div key={device.deviceId} className="flex items-center justify-between py-2 px-3 rounded-lg bg-surface-50 border border-surface-200">
                                  <div className="flex items-center gap-3">
                                    <Cpu size={14} className="text-surface-400" />
                                    <span className="text-sm font-mono text-surface-600">{device.deviceId}</span>
                                    <span className={`text-[10px] font-medium ${isOnline ? "text-success-500" : "text-surface-400"}`}>
                                      {isOnline ? "Online" : "Offline"}
                                    </span>
                                  </div>
                                  <button
                                    onClick={() => handleAddDevice(app.appId, device.deviceId)}
                                    className="flex items-center gap-1 px-2 py-1 text-xs text-brand-500 hover:bg-brand-50 rounded transition-colors cursor-pointer"
                                    title="Add device"
                                  >
                                    <Plus size={12} />
                                    Add
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()}
                    </div>
                    {/* Deploy action */}
                    {hasDevices && app.currentVersion && (
                      <div className="mt-4 pt-4 border-t border-surface-200">
                        <button
                          onClick={() => handleDeploy(app)}
                          disabled={deploying === app.appId}
                          className="flex items-center gap-2 px-4 py-2 bg-brand-500 text-surface-950 text-sm font-medium rounded-lg hover:bg-brand-700 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {deploying === app.appId ? (
                            <Loader size={14} className="animate-spin" />
                          ) : (
                            <Rocket size={14} />
                          )}
                          {deploying === app.appId
                            ? "Deploying..."
                            : `Deploy v${app.currentVersion.slice(0, 8)} to ${deviceCount} device${deviceCount !== 1 ? "s" : ""}`}
                        </button>
                        <p className="mt-1.5 text-[11px] text-surface-400 ml-[30px]">
                          Pushes the latest deployed version to all assigned devices.
                        </p>
                      </div>
                    )}

                    {hasDevices && !app.currentVersion && (
                      <div className="mt-4 pt-4 border-t border-surface-200">
                        <div className="flex items-center gap-2 text-sm text-surface-400">
                          <Clock size={14} />
                          <span>Waiting for first deploy from CI pipeline</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function AppEditForm({
  app,
  onSave,
  onCancel,
  onError,
}: {
  app: App;
  onSave: () => void;
  onCancel: () => void;
  onError: (msg: string) => void;
}) {
  const [displayName, setDisplayName] = useState(app.displayName);
  const [description, setDescription] = useState(app.description);
  const [repo, setRepo] = useState(app.repo);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    setSaving(true);
    try {
      await api.put(`/apps/${app.appId}`, { displayName, description, repo });
      onSave();
    } catch (err: any) {
      onError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-6 p-5 rounded-xl bg-surface-0 border border-brand-200">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-surface-900">
          Edit: {app.displayName}
        </h3>
        <button onClick={onCancel} className="p-1.5 text-surface-400 hover:text-surface-600 rounded-lg hover:bg-surface-100 cursor-pointer">
          <X size={16} />
        </button>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs text-surface-400 mb-1">App ID</label>
          <input
            type="text"
            value={app.appId}
            disabled
            className="w-full px-3 py-2 bg-surface-50 border border-surface-200 rounded-lg text-sm text-surface-500 font-mono opacity-50"
          />
        </div>
        <div>
          <label className="block text-xs text-surface-400 mb-1">Display Name</label>
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            className="w-full px-3 py-2 bg-surface-50 border border-surface-200 rounded-lg text-sm text-surface-900 focus:outline-none focus:border-brand-400"
          />
        </div>
        <div className="col-span-2">
          <label className="block text-xs text-surface-400 mb-1">Description</label>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full px-3 py-2 bg-surface-50 border border-surface-200 rounded-lg text-sm text-surface-900 focus:outline-none focus:border-brand-400"
          />
        </div>
        <div className="col-span-2">
          <label className="block text-xs text-surface-400 mb-1">Repository</label>
          <input
            type="text"
            value={repo}
            onChange={(e) => setRepo(e.target.value)}
            className="w-full px-3 py-2 bg-surface-50 border border-surface-200 rounded-lg text-sm text-surface-900 font-mono focus:outline-none focus:border-brand-400"
          />
        </div>
      </div>

      <div className="mt-4 flex gap-3">
        <button
          onClick={handleSubmit}
          disabled={saving}
          className="px-4 py-2 bg-brand-500 text-surface-950 text-sm font-medium rounded-lg hover:bg-brand-700 transition-colors cursor-pointer disabled:opacity-50"
        >
          {saving ? "Saving..." : "Update"}
        </button>
        <button
          onClick={onCancel}
          className="px-4 py-2 text-sm text-surface-500 hover:text-surface-700 rounded-lg hover:bg-surface-100 transition-colors cursor-pointer"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
